import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, AppState } from '@/state';
import { useEffect } from 'react';
import { fetchListPOS } from '@/state/slice/posSlice';
import { useMsal } from '@azure/msal-react';
import { UserEnum } from '@/state/interface';

// Prevent multiple refresh requests across all hook instances
let isRefreshing = false;

/**
 * Hook to get POS name by ID. Auto-refreshes POS list if ID not found.
 */
export const usePOSName = (posId: string, fallback = ''): string => {
	const dispatch = useDispatch<AppDispatch>();
	const { accounts } = useMsal();
	// Select only the specific POS name to avoid re-renders when other POS entries change
	const posName = useSelector((state: AppState) => state.posState?.posLookup?.[posId]);
	const loading = useSelector((state: AppState) => state.posState?.loading);

	const userType = accounts[0]?.idTokenClaims?.extension_entity_type as string;
	const userID = accounts[0]?.idTokenClaims?.extension_entity_id as string;

	useEffect(() => {
		if (posId && !posName && loading !== 'pending' && !isRefreshing && userType === UserEnum.OUTLET && userID) {
			isRefreshing = true;
			dispatch(fetchListPOS(userID)).finally(() => { isRefreshing = false; });
		}
	}, [posId, posName, loading, userType, userID, dispatch]);

	return posName || fallback;
};

/**
 * Hook to get the complete POS lookup map for batch operations.
 */
export const usePOSLookup = (): Record<string, string> => {
	return useSelector((state: AppState) => state.posState?.posLookup ?? {});
};